import { useEffect } from "react";
import { colors, css, fonts, frames, glyphs, mediaQueriesNumbers, useMediaQuery } from "../../../../../Styles";
import {NewButton, AreaLayout, TableTypes, WaitAnimation, AicoConnection, PageHeader, LoadingSpinner } from "../../../../../Components";
import { ButtonTypes } from "../../../../../Definitions";
import { LC_REBOOT, LCRebootArgs, LCRebootResponse } from "../../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/lcReboot";
import { LAN_CONTROLLER_MQTT_CONFIG, LANControllerMQTTConfigArgs, LANControllerMQTTConfigResponse } from "../../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/lanControllerMQTTConfig";
import { LAN_CONTROLLER_CONFIG, LANControllerConfigArgs, LANControllerConfigResponse } from "../../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/lanControllerConfig";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useAicoConnection } from "../../../../../Components/ToolsComponents/_ToolsComponents/aicoConnectionContext";
import { useTranslation } from "react-i18next";

export default function LANControllerConfig() {
	const { t } = useTranslation();
	const mobileXs = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileXs}px)`); 
	
	const { state: connectionState } = useAicoConnection();

	const [configFetch, { data: configData, loading: configLoading, error: configError }] = useLazyQuery<LANControllerConfigResponse, LANControllerConfigArgs>(LAN_CONTROLLER_CONFIG, {
		fetchPolicy: "network-only"
	});

	const [mqttFetch, { data: mqttData, loading: mqttLoading, error: mqttError }] = useLazyQuery<LANControllerMQTTConfigResponse, LANControllerMQTTConfigArgs>(LAN_CONTROLLER_MQTT_CONFIG, {
		fetchPolicy: "network-only"
	});

	const [rebootFetch, { data: rebootData, loading: rebootLoading, error: rebootError }] = useMutation<LCRebootResponse, LCRebootArgs>(LC_REBOOT, {
		fetchPolicy: "network-only"
	});

	useEffect(() => {
		if (connectionState.deviceConnection) {
			configFetch({ variables: connectionState.deviceConnection });
			mqttFetch({ variables: connectionState.deviceConnection });
		}
	}, [connectionState.deviceConnection]);

	const loading = configLoading || mqttLoading;

	const error = configError || mqttError;

	return (
		<frames.Page_inner>
				<PageHeader topHeader='Aico Tables' subHeader='Config' />
			<frames._page_inner_content>

				<AreaLayout
					tableType={TableTypes.fitContent}
					headerArea={mobileXs ? undefined : <AicoConnection inTable/>}
					buttonArea={<div style={{ display: "flex", gap: "5px" }}>
						{mobileXs && <AicoConnection inTable/>}
						<NewButton
							content={"fetch"}
							buttonType={ButtonTypes.tableButton}
							startEnhancer={glyphs.Fetch({})}
							onClick={() => {
								if (connectionState.deviceConnection) {
									configFetch({ variables: connectionState.deviceConnection });
									mqttFetch({ variables: connectionState.deviceConnection });
								}
							}}
							disabled={loading}
						/>
					</div>}
					contentArea={<div style={{ display: "flex", flexDirection: "column", gap: "50px", height: "100%", alignItems: "center" }}>
						<NewButton
							content={"Reboot"}
							disabled={rebootLoading}
							onClick={() => {
								if (connectionState.deviceConnection) {
									rebootFetch({ variables: connectionState.deviceConnection });
								}
							}}
							overrides={{
								baseStyles: { width: "fit-content", backgroundColor: colors.greyTertiary, height: "30px", padding: "0 10px" },
								contentStyles: { color: colors.greyDarkPrimary, textShadow: "none", ...fonts.fontSize4 },
								hoverStyles: { backgroundColor: colors.greyLightPrimary, color: colors.greyDarkPrimary }
							}}
						/>

						<div style={{ width: "100%", maxWidth: "500px" }}>
							<div style={{ borderBottom: "2px solid " + colors.greyLightPrimary, textAlign: "left", ...fonts.fontSize3, ...fonts.header, color: colors.greyDarkPrimary }}>config </div>
							{loading ? <WaitAnimation/> : error ? <div style={{ ...css.error }}>Error</div> :
								<div style={{ textAlign: "left", ...fonts.fontSize4, ...fonts.paragraph, color: colors.greyDarkPrimary }}>
									{configData?.controller.config ? ( <>
										<div>CPU ID: {configData.controller.config.cpuId} </div>
										<div>MAC Address: {configData.controller.config.macAddress} </div>
										<div>IP Address: {configData.controller.config.ipAddress} </div>
										<div>Firmware Version: {configData.controller.config.firmwareVersion} </div>
										<div>Server Protocol Version: {configData.controller.config.serverProtocolVersion} </div>
										<div>Table Definition Version: {configData.controller.config.tableDefinitionVersion} </div>
										<div>Product Name: {configData.controller.config.productName} </div>
										<div>Hardware Version: {configData.controller.config.hardwareVersion} </div>
										<div>Production Date: {configData.controller.config.productionDate} </div>
									</> ) : (<div style={{...css.emptyPen, textAlign: "left", opacity: "0.5" }}>{t("noData", { defaultValue: "no data" })}</div>)}
								</div>
							}
						</div>
						
						<div style={{ width: "100%", maxWidth: "500px" }}>
							<div style={{ borderBottom: "2px solid " + colors.greyLightPrimary, textAlign: "left", ...fonts.fontSize3, ...fonts.header, color: colors.greyDarkPrimary }}>mqtt config </div>
							{loading ? <WaitAnimation /> : error ? <div style={{ ...css.error }}>Error</div> :
								<div style={{ textAlign: "left", ...fonts.fontSize4, ...fonts.paragraph, color: colors.greyDarkPrimary }}>
									{mqttData?.controller.mqttConfig ? ( <>
										<div>Domain: {mqttData.controller.mqttConfig.domain} </div>
										<div>Password: {mqttData.controller.mqttConfig.password} </div>
										<div>IP Address: {mqttData.controller.mqttConfig.ipAddress} </div>
									</> ) : (<div style={{ ...css.emptyPen, textAlign: "left", opacity: "0.5" }}>{t("noData", { defaultValue: "no data" })}</div>)}
								</div>
							}
						</div>


						{rebootLoading && <LoadingSpinner/>}
						{(rebootError
							|| (rebootData?.controller && !rebootData.controller.reboot?.success)
						)
							&& <p style={{ ...css.error, maxWidth: "500px" }}>Error: Failed to reboot device</p>
						}
						{!rebootLoading
							&& !rebootError
							&& rebootData?.controller.reboot?.success
							&& <p style={{ ...css.success, maxWidth: "500px" }}>Succesfully rebooted LC</p>
						}
					</div>}
				/>
			</frames._page_inner_content>
		</frames.Page_inner>
	)
}